import { reportManualError } from '@/platform/telemetry/frontend-alert';
import * as v from 'valibot';

// Observe-only runtime validation of an API response against a valibot schema.
//
// Port of web_frontend/src/utils/validate-response.js so the Pattern-6 contract
// layer survives the v3 migration: legacy has six wired schemas plus two CI gates
// (schema-drift, proto-depth) hanging off `config.schema`, and every page moved to
// v3 was silently dropping that protection because the v3 http layer had no
// equivalent hook.
//
// On a mismatch it reports drift (console + the existing frontend-alert telemetry)
// but NEVER throws or alters the response — a backend contract drift (renamed /
// dropped / retyped field) surfaces as a signal instead of a silent NaN or blank
// page, with zero risk of the validation itself breaking the page it protects.
// Opt in per request via `config.schema` (see the axios module augmentation).
//
// VALIBOT, not zod, and typed rather than duck-typed. v3 already ships valibot
// (shared/lib/url-params/codec.ts), so pulling zod in for this would have put two
// validation libraries in the bundle — +12.3 KB gzip on an eager chunk, measured.
// The earlier draft accepted anything exposing a `safeParse` METHOD, which was a
// trap: valibot's safeParse is a standalone `v.safeParse(schema, input)`, so a
// valibot schema would have failed that check and the validator would have
// silently returned — no validation, no report, no error. Taking a
// `v.GenericSchema` makes the wrong thing a compile error instead of a silent
// no-op, which is the failure mode this whole layer exists to prevent.

export type ResponseSchema = v.GenericSchema<unknown, unknown>;

export interface DriftMeta {
  url?: string;
  method?: string;
}

/** Only the first N issues/paths travel with the report; drift is usually repetitive. */
const MAX_REPORTED = 10;

/** `issues[].path` is a list of path ITEMS carrying `key`, not a list of strings. */
function issuePath(issue: v.BaseIssue<unknown>): string {
  const items = issue.path;
  if (!items?.length) return '(root)';
  return items.map((item) => String(item.key)).join('.');
}

export function validateResponseSchema(
  data: unknown,
  schema: ResponseSchema | undefined,
  meta: DriftMeta = {},
): void {
  if (!schema) return;

  let result: v.SafeParseResult<ResponseSchema>;
  try {
    result = v.safeParse(schema, data);
  } catch {
    // Validation must never break the response path; swallow any internal error.
    return;
  }
  if (result.success) return;

  // `issues` needs no fallback and `message` needs no default: SafeParseResult is a
  // union discriminated on `success`, so the early return above narrows this to the
  // failure arm where `issues` is a non-empty tuple, and valibot types BaseIssue's
  // `message` as a required string.
  const { issues } = result;
  const formatted = issues
    .slice(0, MAX_REPORTED)
    .map((issue) => `${issuePath(issue)}: ${issue.message}`);

  // frontend-alert dedups by a fingerprint of (type + message + filename + lineno),
  // NOT by the detail payload. A constant message would collapse every drift onto one
  // fingerprint, so a second distinct drift in the same session would be dropped as a
  // duplicate. Folding the endpoint + the drifted field paths into the message gives
  // distinct (endpoint, field-set) → distinct fingerprint, while the SAME drift
  // repeated still dedups instead of flooding.
  const where = meta.url ?? 'unknown';
  const paths = [...new Set(issues.map(issuePath))].slice(0, MAX_REPORTED);

  console.warn('[schema_drift]', where, formatted);
  reportManualError('schema_drift', `Response schema drift @ ${where} [${paths.join(',')}]`, {
    url: meta.url,
    method: meta.method,
    issues: formatted,
  });
}
